import React from 'react';
import {
  ArrowDownCircle,
  ArrowUpCircle,
  BarChart3,
  CircleDollarSign,
  IndianRupee,
  LockKeyhole,
  ReceiptText,
  Target,
  TrendingDown,
  TrendingUp,
  Wallet,
} from 'lucide-react';

import type {
  PassbookSummary,
  PlayerOverview,
} from '../types';

interface PlayerAnalyticsProps {
  player: PlayerOverview | null;
  summary: PassbookSummary | null;
  loading?: boolean;
}

const formatCurrency = (value: number | null | undefined) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatPercentage = (value: number | null | undefined) =>
  `${Number(value || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}%`;

const PlayerAnalytics: React.FC<PlayerAnalyticsProps> = ({
  player,
  summary,
  loading = false,
}) => {
  const totalBets = Number(summary?.total_bets || 0);
  const totalWagered = Number(summary?.total_wagered || 0);
  const averageBet = totalBets > 0 ? totalWagered / totalBets : 0;
  const netProfit = Number(summary?.net_profit || 0);

  const cards = [
    {
      label: 'Current Wallet',
      value: formatCurrency(player?.wallet.total),
      icon: Wallet,
    },
    {
      label: 'Available Balance',
      value: formatCurrency(player?.wallet.available),
      icon: IndianRupee,
    },
    {
      label: 'Locked Balance',
      value: formatCurrency(player?.wallet.locked),
      icon: LockKeyhole,
    },
    {
      label: 'Lifetime Deposits',
      value: formatCurrency(summary?.total_deposits),
      icon: ArrowDownCircle,
    },
    {
      label: 'Lifetime Withdrawals',
      value: formatCurrency(summary?.total_withdrawals),
      icon: ArrowUpCircle,
    },
    {
      label: 'Total Wagered',
      value: formatCurrency(summary?.total_wagered),
      icon: Target,
    },
    {
      label: 'Total Won',
      value: formatCurrency(summary?.total_won),
      icon: TrendingUp,
    },
    {
      label: 'Total Lost',
      value: formatCurrency(summary?.total_lost),
      icon: TrendingDown,
    },
    {
      label: 'Net Profit',
      value: formatCurrency(summary?.net_profit),
      icon: BarChart3,
      valueClass:
        netProfit >= 0 ? 'text-emerald-600' : 'text-red-600',
    },
    {
      label: 'ROI',
      value: formatPercentage(summary?.roi_percentage),
      icon: CircleDollarSign,
      valueClass:
        Number(summary?.roi_percentage || 0) >= 0
          ? 'text-emerald-600'
          : 'text-red-600',
    },
    {
      label: 'Total Bets',
      value: totalBets.toLocaleString('en-IN'),
      icon: ReceiptText,
    },
    {
      label: 'Average Bet',
      value: formatCurrency(averageBet),
      icon: Target,
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-slate-900">
          Player Analytics
        </h2>
        <p className="mt-1 text-sm text-slate-500">
          Lifetime wallet, betting and financial performance.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((card) => {
          const Icon = card.icon;

          return (
            <div
              key={card.label}
              className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                    {card.label}
                  </p>

                  <p
                    className={`mt-3 text-2xl font-semibold ${
                      card.valueClass || 'text-slate-900'
                    }`}
                  >
                    {loading ? 'Loading...' : card.value}
                  </p>
                </div>

                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlayerAnalytics;
